import {
  CylinderGeometry,
  Group,
  IcosahedronGeometry,
  Mesh,
  MeshLambertMaterial,
  SphereGeometry,
  type BufferGeometry,
  type Material,
} from 'three';

/**
 * EN-11 Hipopótamo de la Hacienda (GDD §8.3, issue #87): a friendly round caricature with big eyes and a
 * yawning mouth (no aggressive teeth, no wounds: GDD §3.4), its lagoon (a flat water disc, no collision) and
 * a handful of bubbles. The model spans about 1.8 x 1.2 u; the hitbox is 1.4 x 0.9 u (70-80 %).
 */
export interface Hippo {
  body: Group;
  lagoon: Mesh;
  bubbles: Group;
  dispose(): void;
}

export const HIPPO_COLORS = { skin: 0x8c7a96, belly: 0xb9a9bd, eye: 0xf4f1ea, pupil: 0x15171b, mouth: 0x6b3b4a, water: 0x3f7f9a, bubble: 0xd6eef5 } as const;

/** The plush toy of El Patrón (#95, #96): the same silhouette in a pink felt palette. */
export const PLUSH_COLORS = { ...HIPPO_COLORS, skin: 0xe39ab9, belly: 0xf6d3e2, mouth: 0x9c5a78 } as const;

export function buildHippo(colors: typeof HIPPO_COLORS | typeof PLUSH_COLORS = HIPPO_COLORS, bodyName = 'enemy-en11-body'): Hippo {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const paint = (color: number, extra: { transparent?: boolean; opacity?: number } = {}) => {
    const material = new MeshLambertMaterial({ color, flatShading: true, ...extra });
    materials.push(material);
    return material;
  };
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };

  const body = new Group();
  body.name = bodyName;
  const torso = new Mesh(own(new IcosahedronGeometry(0.6, 1)), paint(colors.skin));
  torso.scale.set(1.5, 1, 1);
  const belly = new Mesh(own(new IcosahedronGeometry(0.45, 0)), paint(colors.belly));
  belly.scale.set(1.5, 0.8, 0.9);
  belly.position.set(0, -0.15, 0.2);
  const snout = new Mesh(own(new IcosahedronGeometry(0.4, 0)), paint(colors.skin));
  snout.scale.set(1.2, 0.8, 1);
  snout.position.set(-0.75, -0.05, 0);
  const mouth = new Mesh(own(new IcosahedronGeometry(0.2, 0)), paint(colors.mouth));
  mouth.position.set(-1.05, -0.05, 0);
  const eyeMaterial = paint(colors.eye);
  const pupilMaterial = paint(colors.pupil);
  for (const z of [0.3, -0.3]) {
    const eye = new Mesh(own(new SphereGeometry(0.13, 6, 4)), eyeMaterial);
    eye.position.set(-0.55, 0.38, z);
    const pupil = new Mesh(own(new SphereGeometry(0.06, 5, 3)), pupilMaterial);
    pupil.position.set(-0.63, 0.38, z);
    body.add(eye, pupil);
  }
  const ear = new Mesh(own(new IcosahedronGeometry(0.1, 0)), paint(colors.skin));
  ear.position.set(-0.2, 0.6, 0.25);
  body.add(torso, belly, snout, mouth, ear);

  const lagoon = new Mesh(own(new CylinderGeometry(1.7, 1.7, 0.04, 12)), paint(colors.water, { transparent: true, opacity: 0.8 }));
  lagoon.name = 'enemy-en11-lagoon';

  const bubbles = new Group();
  bubbles.name = 'enemy-en11-bubbles';
  const bubbleMaterial = paint(colors.bubble, { transparent: true, opacity: 0.7 });
  [[-0.6, 0.1, 0.1], [0.1, 0.3, -0.1], [0.6, 0.15, 0.05], [-0.1, 0.5, 0.1]].forEach(([x, y, size]) => {
    const bubble = new Mesh(own(new SphereGeometry(size!, 5, 4)), bubbleMaterial);
    bubble.position.set(x!, y!, 0.2);
    bubbles.add(bubble);
  });

  body.visible = false;
  bubbles.visible = false;
  return {
    body,
    lagoon,
    bubbles,
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
